import { bootstrapApplication } from '@angular/platform-browser';
import { registerLocaleData } from '@angular/common';
import localeAr from '@angular/common/locales/ar-SA';
import { App } from './app/app';
import { appConfig } from './app/app.config';
import { ThemeService } from './app/core/services/theme.service';
import * as Sentry from '@sentry/angular';
import { environment } from './environments/environment.prod';

// Initialize Sentry BEFORE Angular bootstraps
if (environment.sentry?.enabled) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.sentry.environment,
    release: `amancare-frontend@${environment.version}`,

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
      }),
    ],

    tracesSampleRate: environment.sentry.tracesSampleRate,
    replaysSessionSampleRate: environment.sentry.replaysSessionSampleRate,
    replaysOnErrorSampleRate: environment.sentry.replaysOnErrorSampleRate,
    debug: environment.sentry.debug,
    sendDefaultPii: false,

    // FIX #1: The type is inferred correctly now because scrubPII returns ErrorEvent
    beforeSend: environment.sentry.beforeSend
      ? (event, hint) => {
        return scrubPII(event, hint);
      }
      : undefined,

    beforeBreadcrumb: (breadcrumb, hint) => {
      return scrubBreadcrumbPII(breadcrumb, hint);
    },

    ignoreErrors: [
      'top.GLOBALS',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      'Request aborted',
      'cancelled',
      'Non-Error promise rejection captured',
      'Non-Error exception captured',
    ],

    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],

    // FIX #2: Removed 'maxBatchSize' as it is no longer a valid property in transportOptions
    // If you don't have other transport options, you can remove this object entirely.
    transportOptions: {
       // fetchOptions: { ... } // Only valid fetch options go here now
    },
  });
}

// FIX #1: Changed Sentry.Event to Sentry.ErrorEvent
function scrubPII(event: Sentry.ErrorEvent, hint?: Sentry.EventHint): Sentry.ErrorEvent | null {
  // Scrub patient data from error messages
  if (event.message) {
    event.message = scrubSensitiveText(event.message);
  }

  // Scrub exception messages
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map(exception => ({
      ...exception,
      value: exception.value ? scrubSensitiveText(exception.value) : exception.value
    }));
  }

  // Scrub request data
  if (event.request) {
    if (event.request.data) {
      event.request.data = scrubObject(event.request.data);
    }
    if (event.request.headers) {
      delete event.request.headers['Authorization'];
      delete event.request.headers['authorization'];
      delete event.request.headers['Cookie'];
      delete event.request.headers['cookie'];
    }
  }

  if (event.extra) {
    event.extra = scrubObject(event.extra);
  }

  if (event.contexts) {
    event.contexts = scrubObject(event.contexts);
  }

  return event;
}

// ... rest of your code (scrubBreadcrumbPII, scrubSensitiveText, etc) remains the same
function scrubBreadcrumbPII(breadcrumb: Sentry.Breadcrumb, hint?: Sentry.BreadcrumbHint): Sentry.Breadcrumb | null {
  if (breadcrumb.data?.['url']) {
    const url = String(breadcrumb.data['url']);
    if (url.includes('/health') || url.includes('/ping') || url.includes('/validate')) {
      return null;
    }
  }

  if (breadcrumb.data) {
    breadcrumb.data = scrubObject(breadcrumb.data);
  }

  if (breadcrumb.message) {
    breadcrumb.message = scrubSensitiveText(breadcrumb.message);
  }

  return breadcrumb;
}

function scrubSensitiveText(text: string): string {
  text = text.replace(/(\+?967)?[75][0-9]{8}/g, '[PHONE_REDACTED]');
  text = text.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL_REDACTED]');
  text = text.replace(/\b\d{10}\b/g, '[ID_REDACTED]');
  text = text.replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4,7}\b/g, '[CARD_REDACTED]');
  return text;
}

function scrubObject(obj: any): any {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const sensitiveKeys = [
    'password', 'token', 'jwt', 'apiKey', 'secret', 'authorization',
    'accessToken', 'refreshToken', 'sessionId',
    'firstName', 'lastName', 'fullName', 'nationalId', 'passportNumber',
    'phone', 'mobile', 'telephone', 'email', 'address',
    'patientName', 'patientId', 'userId', 'userName', 'username',
    'diagnosis', 'prescription', 'medicalHistory', 'symptoms', 'bloodType',
    'allergies', 'medications', 'treatment', 'condition',
    'creditCard', 'cardNumber', 'cvv', 'accountNumber', 'bankAccount',
    'الاسم', 'البريد', 'الهاتف', 'العنوان', 'كلمةالمرور', 'كلمة_المرور',
  ];

  const scrubbed: any = Array.isArray(obj) ? [] : {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const lowerKey = key.toLowerCase();
      if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive.toLowerCase()))) {
        scrubbed[key] = '[REDACTED]';
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        scrubbed[key] = scrubObject(obj[key]);
      } else if (typeof obj[key] === 'string') {
        scrubbed[key] = scrubSensitiveText(obj[key]);
      } else {
        scrubbed[key] = obj[key];
      }
    }
  }
  return scrubbed;
}

ThemeService.preloadTheme();
registerLocaleData(localeAr);

bootstrapApplication(App, appConfig)
  .catch(err => {
    console.error('خطأ في تشغيل التطبيق:', err);
    if (environment.sentry?.enabled) {
      Sentry.captureException(err);
    }
  });
